import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Loader2, ChevronRight, Sparkles } from 'lucide-react';
import DiscountedPrice from '../components/DiscountedPrice';
import { compressForAI } from '../utils/compressImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRICE = 500;

export default function AmbientLightingPage() {
  const navigate  = useNavigate();
  const fileRef   = useRef(null);
  const sliderRef = useRef(null);

  const [beforeUrl,  setBeforeUrl]  = useState(null);
  const [afterB64,   setAfterB64]   = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sliderPos,  setSliderPos]  = useState(50);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setBeforeUrl(dataUrl);
      setAfterB64(null);
      setSliderPos(50);
      setGenerating(true);
      const base64 = await compressForAI(dataUrl);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/ambient-lighting/preview`, {
          image_data: base64,
        }, { timeout: 60000 });
        if (res.data.image) setAfterB64(res.data.image);
      } catch { } finally { setGenerating(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleSlider = (e) => {
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    setSliderPos(Math.max(5, Math.min(95, (x / rect.width) * 100)));
  };

  return (
    <div className="min-h-screen bg-[#000000] text-white">
      <div className="max-w-lg mx-auto px-4 pt-6 pb-28">

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate('/')} data-testid="ambient-back-btn">
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold leading-tight">Ambient Lighting</h1>
            <p className="text-zinc-500 text-xs">Premium LED interior atmosphere</p>
          </div>
        </div>

        {/* Upload / Slider area */}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />

        {generating ? (
          <div className="w-full border-2 border-dashed border-zinc-700 rounded-2xl py-14 flex flex-col items-center gap-3 bg-zinc-900/30 mb-4">
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
            <p className="text-zinc-400 text-sm">Applying ambient lighting to your vehicle…</p>
          </div>
        ) : beforeUrl && afterB64 ? (
          <div
            ref={sliderRef}
            className="relative rounded-2xl overflow-hidden border border-zinc-800 mb-4 cursor-col-resize select-none"
            style={{ aspectRatio: '4/3', touchAction: 'none' }}
            onMouseMove={handleSlider}
            onTouchMove={handleSlider}
          >
            {/* Background = AI ambient result (shows on RIGHT) */}
            <img src={`data:image/png;base64,${afterB64}`} alt="After ambient lighting" className="absolute inset-0 w-full h-full object-cover" />
            {/* Overlay = original photo, clipped to LEFT portion */}
            <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
              <img src={beforeUrl} alt="Original" className="w-full h-full object-cover" />
            </div>
            <div className="absolute top-0 bottom-0 w-0.5 bg-white/80" style={{ left: `${sliderPos}%` }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border-2 border-zinc-300 flex items-center justify-center shadow-xl">
                <span className="text-black text-sm font-black">↔</span>
              </div>
            </div>
            <span className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full font-semibold">Original</span>
            <span className="absolute top-3 right-3 bg-amber-500/90 text-black text-xs px-2.5 py-1 rounded-full font-semibold">Ambient Lit</span>
            <button
              onClick={() => { setAfterB64(null); setBeforeUrl(null); fileRef.current?.click(); }}
              className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-white font-medium hover:bg-black/90 transition-colors"
            >
              <Camera className="w-3.5 h-3.5" /> Change photo
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="ambient-upload-area"
            className="w-full border-2 border-dashed border-zinc-700 rounded-2xl py-14 px-6 flex flex-col items-center gap-3 bg-zinc-900/30 hover:border-amber-500/40 transition-all active:scale-[0.99] mb-4"
          >
            <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Camera className="w-8 h-8 text-black" />
            </div>
            <p className="text-amber-400 font-bold text-base text-center">Upload a Photo of Your Interior</p>
            <p className="text-zinc-500 text-sm text-center">See ambient lighting added to your vehicle</p>
          </button>
        )}

        {/* Package info card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 mb-4">
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-2">Package</p>
          <p className="text-amber-400 text-xl font-black text-center">Ambient Lighting</p>
          <p className="text-zinc-400 text-xs text-center mt-0.5 mb-3">Custom LED interior lighting</p>
          <div className="border-t border-zinc-800 pt-3 mb-3 space-y-2">
            {['Adjustable colors via App', 'Doors, dashboard, and footwell lighting', 'Professional install with clean wiring', 'Factory-look finish, dimmable'].map(f => (
              <div key={f} className="flex items-start gap-2">
                <span className="text-zinc-500 text-xs mt-0.5">•</span>
                <span className="text-white text-xs">{f}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-800 pt-3 text-center">
            <span className="text-amber-400 font-black text-3xl">
              <DiscountedPrice price={PRICE} category="Ambient Lighting" size="lg" />
            </span>
          </div>
        </div>

        {/* BOOK NOW */}
        <button
          data-testid="ambient-book-now-btn"
          onClick={() => navigate('/?openBooking=Ambient+Lighting')}
          className="w-full py-4 bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-black font-black text-base rounded-2xl flex items-center justify-center gap-2 transition-all"
        >
          BOOK NOW · ${PRICE}
          <ChevronRight className="w-5 h-5" />
        </button>

        <p className="text-zinc-600 text-xs text-center mt-4">
          Next: pick your date &amp; time, then complete secure payment.
        </p>
      </div>
    </div>
  );
}
